import { NOT_SCHEDULED_DATE, orderTypeConstants } from "constants/order";

/**
 * CLE-3043 follow-up — what the staff member has typed into the New Estimate form, for Clear Health
 * AI (Ani, 2026-11-02: on the create page the bot must "identify what patient are you talking
 * about"). Published by usePublishCreateEstimateBlockers into StaffChatPageHintsContext and sent by
 * StaffChatWidget as `data.page.form`.
 *
 * Only these fields, each omitted when empty: the patient's first and last name and date of birth,
 * the Account/Visit ID, the estimate type, the date of service (or Not scheduled), the primary and
 * (when switched on) secondary payer and member ID, the procedures and the total. Never the phone,
 * email, benefits or anything else on the form.
 *
 * Every text value is cut to the API's length and stripped to the API's character allow-list
 * (FORM_TEXT_PATTERN in services/clear-api/src/domains/chats/chat.dto.ts): the API splices the values
 * into the bot's system prompt and DROPS a value with another character, so a real value always
 * reaches the bot. The payer's name is sent for completeness only — the API reads it from the payer
 * id itself and never uses the one sent.
 */
export interface IEstimateFormSnapshot {
  patient?: { firstName?: string; lastName?: string; dateOfBirth?: string };
  accountNumber?: string;
  orderType?: string;
  dateOfService?: string;
  notScheduled?: boolean;
  payer?: { payerId: number; name?: string };
  secondaryPayer?: { payerId: number; name?: string };
  memberId?: string;
  secondaryMemberId?: string;
  procedures?: { code: string; description?: string; priceInCents?: number }[];
  totalAmountInCents?: number;
}

/** The shared contract with the API (MAX_FORM_PROCEDURES / FORM_TEXT_LIMITS in chat.dto.ts). */
export const MAX_SNAPSHOT_PROCEDURES = 30;
export const SNAPSHOT_TEXT_LIMITS = {
  name: 80,
  memberId: 40,
  accountNumber: 40,
  description: 120,
} as const;

const LETTER_OR_DIGIT = "A-Za-z0-9À-ÖØ-öø-ɏ";
/** Every character outside the API's allow-list: no `<`, `>`, backticks, braces, quotes, newlines. */
const DISALLOWED_TEXT = new RegExp(`[^${LETTER_OR_DIGIT} ,.'’()/&:%+#-]+`, "g");
const HAS_LETTER_OR_DIGIT = new RegExp(`[${LETTER_OR_DIGIT}]`);
const PROCEDURE_CODE = /^[A-Za-z0-9]{3,7}$/;
const ORDER_TYPES = Object.values(orderTypeConstants);

/** A typed value as the API accepts it: disallowed characters become a space, trimmed, cut to `max`. */
export const toSnapshotText = (
  value: unknown,
  max: number
): string | undefined => {
  if (typeof value !== "string" && typeof value !== "number") return undefined;
  // NFC first: a name pasted with a combining accent ("Rene" + U+0301) becomes "René", not "Rene e".
  const cleaned = String(value)
    .normalize("NFC")
    .replace(DISALLOWED_TEXT, " ")
    .replace(/ {2,}/g, " ")
    .trim();
  if (!HAS_LETTER_OR_DIGIT.test(cleaned)) return undefined;
  return cleaned.length > max ? cleaned.slice(0, max).trimEnd() : cleaned;
};

const isRealDate = (y: number, m: number, d: number): boolean => {
  const date = new Date(Date.UTC(y, m - 1, d));
  return (
    date.getUTCFullYear() === y &&
    date.getUTCMonth() === m - 1 &&
    date.getUTCDate() === d
  );
};

const pad = (n: number): string => String(n).padStart(2, "0");

/**
 * A date the form holds as `YYYY-MM-DD`. The form's date fields are masked "MM/DD/YYYY" text (a
 * half-typed one is no date); a saved order may also hold an ISO string or a Date.
 */
export const toSnapshotDate = (value: unknown): string | undefined => {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return undefined;
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(
      value.getDate()
    )}`;
  }
  if (typeof value !== "string") return undefined;
  const us = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
  if (us) {
    const [m, d, y] = [Number(us[1]), Number(us[2]), Number(us[3])];
    return isRealDate(y, m, d) ? `${y}-${pad(m)}-${pad(d)}` : undefined;
  }
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (iso) {
    const [y, m, d] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
    return isRealDate(y, m, d) ? `${y}-${pad(m)}-${pad(d)}` : undefined;
  }
  return undefined;
};

const NOT_SCHEDULED_ISO = toSnapshotDate(NOT_SCHEDULED_DATE);

const toCents = (value: unknown): number | undefined => {
  const n = typeof value === "string" && value.trim() ? Number(value) : value;
  return typeof n === "number" && Number.isFinite(n) && n >= 0
    ? Math.round(n)
    : undefined;
};

const toPayer = (
  value: unknown
): { payerId: number; name?: string } | undefined => {
  if (!value || typeof value !== "object") return undefined;
  const { payerId, name } = value as { payerId?: unknown; name?: unknown };
  if (
    typeof payerId !== "number" ||
    !Number.isSafeInteger(payerId) ||
    payerId <= 0
  ) {
    return undefined;
  }
  const safeName = toSnapshotText(name, SNAPSHOT_TEXT_LIMITS.name);
  return safeName ? { payerId, name: safeName } : { payerId };
};

const toProcedures = (value: unknown): IEstimateFormSnapshot["procedures"] => {
  if (!Array.isArray(value)) return undefined;
  const procedures: NonNullable<IEstimateFormSnapshot["procedures"]> = [];
  value.forEach((item) => {
    if (procedures.length >= MAX_SNAPSHOT_PROCEDURES) return;
    if (!item || typeof item !== "object") return;
    const { code, cptDescription, amountInCents } = item as Record<
      string,
      unknown
    >;
    const trimmedCode = typeof code === "string" ? code.trim() : "";
    if (!PROCEDURE_CODE.test(trimmedCode)) return;
    const description = toSnapshotText(
      cptDescription,
      SNAPSHOT_TEXT_LIMITS.description
    );
    const priceInCents = toCents(amountInCents);
    procedures.push({
      code: trimmedCode,
      ...(description ? { description } : {}),
      ...(priceInCents !== undefined ? { priceInCents } : {}),
    });
  });
  return procedures.length ? procedures : undefined;
};

/**
 * The snapshot of the form's current values (react-hook-form `getValues()`), or `null` when nothing
 * that says who or what the estimate is for has been typed — no first or last name, no payer, no
 * date of service and no procedure. `null` sends nothing, and the API then behaves as before.
 *
 * The payer and member ID are read as `mapFormToRequestBody` saves them: the payer for a Patient
 * Estimate only (getPayerIdByOrderType), the secondary only when it is switched on.
 */
export const buildEstimateFormSnapshot = (
  values: Record<string, any> | null | undefined
): IEstimateFormSnapshot | null => {
  if (!values) return null;
  const snapshot: IEstimateFormSnapshot = {};

  const firstName = toSnapshotText(values.firstName, SNAPSHOT_TEXT_LIMITS.name);
  const lastName = toSnapshotText(values.lastName, SNAPSHOT_TEXT_LIMITS.name);
  const dateOfBirth = toSnapshotDate(values.dateOfBirth);
  if (firstName || lastName || dateOfBirth) {
    snapshot.patient = {
      ...(firstName ? { firstName } : {}),
      ...(lastName ? { lastName } : {}),
      ...(dateOfBirth ? { dateOfBirth } : {}),
    };
  }

  const accountNumber = toSnapshotText(
    values.accountNumber,
    SNAPSHOT_TEXT_LIMITS.accountNumber
  );
  if (accountNumber) snapshot.accountNumber = accountNumber;

  const orderType = ORDER_TYPES.includes(values.orderType)
    ? (values.orderType as string)
    : undefined;
  if (orderType) snapshot.orderType = orderType;

  const dateOfService = toSnapshotDate(values.dateOfService);
  if (values.notScheduledYet === true || dateOfService === NOT_SCHEDULED_ISO) {
    snapshot.notScheduled = true;
  } else if (dateOfService) {
    snapshot.dateOfService = dateOfService;
  }

  const takesPayer = !orderType || orderType === orderTypeConstants.GFE;
  const payer = takesPayer ? toPayer(values.payer) : undefined;
  if (payer) snapshot.payer = payer;
  const memberId = toSnapshotText(
    values.memberId,
    SNAPSHOT_TEXT_LIMITS.memberId
  );
  if (memberId) snapshot.memberId = memberId;

  if (takesPayer && values.secondaryInsuranceActivated) {
    const secondaryPayer = toPayer(values.payer2);
    if (secondaryPayer) snapshot.secondaryPayer = secondaryPayer;
    const secondaryMemberId = toSnapshotText(
      values.memberId2,
      SNAPSHOT_TEXT_LIMITS.memberId
    );
    if (secondaryMemberId) snapshot.secondaryMemberId = secondaryMemberId;
  }

  const procedures = toProcedures(values.serviceDetails);
  if (procedures) snapshot.procedures = procedures;

  const totalAmount =
    values.totalAmount === null ||
    values.totalAmount === undefined ||
    String(values.totalAmount).trim() === ""
      ? undefined
      : toCents(Number(values.totalAmount) * 100);
  if (totalAmount !== undefined) snapshot.totalAmountInCents = totalAmount;

  const saysWho =
    !!snapshot.patient?.firstName ||
    !!snapshot.patient?.lastName ||
    !!snapshot.payer ||
    !!snapshot.dateOfService ||
    !!snapshot.procedures;
  return saysWho ? snapshot : null;
};
